import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Spin, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Modal from "components/Modal";
import AddNewAreas from "../../modals/AddNewAreas";
import DeleteArea from "../../modals/DeleteArea";
import UpdateAreas from "../../modals/UpdateAreas";
import { useTranslation } from "react-i18next";
import { UTILS } from "utils";
import { CONSTANTS as C } from "config";
import useUserType from "hooks/useUserType";
import useAllStores from "hooks/useCurrentUserStores";
import { AreasAction } from "redux/actions";
import BackButton from "components/BackButton";
import PlusIcon from "features/icons/PlusIcon";
import RenderTime from "features/shipping_methods/common/RenderTime";
import AppHeading1 from "components/ui/AppHeading1";
import AppSearchInput from "components/ui/AppSearchInput";
import AppPaginatedTable from "components/ui/AppPaginatedTable";
import AddDeliveryAreaModal from "./addDeliveryAreaModal";
import DeleteDeliveryAreaModal from "./deleteDeliveryAreaModal";
import EditDeliveryAreaModal from "./editDeliveryAreaModal";
import AppButton from "components/ui/AppButton";
import AppIcon from "components/ui/AppIcon";
import { API } from "api";
import { errorHandler } from "api/api/errorHandler";
function AreasTable({onRefreshAreas}) {
  const dispatch = useDispatch();
  const userType = useUserType() || UTILS.getCurrentUser()?.userType;
  const { addedAreas, selectedStore } = useSelector(
    (state) => state.AreasReducer
  );
  const { isLoading } = useSelector((state) => state.LoaderReducer);
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const [searchTerm, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openAddDeliveryAreaModal, setOpenAddDeliveryAreaModal] = useState(false);
  const [dialogContentId, setDialogContentId] = useState(-1);
  const [selectedRowKeys, setSelectedRowsKeys] = useState([]);
  const [selectedDataRows, setSelectedDataRows] = useState({});
  const [openEditDeliveryAreaModal, setOpenEditDeliveryAreaModal]=useState(false)
    const [selectedArea, setSelectedArea] = useState(null);
    const [deletingAreaId, setDeletingAreaId] = useState(null);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [isBulkDelete, setIsBulkDelete] = useState(false);
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        area: null,
    });
  const { stores } = useAllStores();
  const { t } = useTranslation();
  const [data, setData] = useState([]);

  const columns = [
    {
      key: "areaID",
      title: t("Area Name"),
      dataIndex: "areaName",
      sorter: (a, b) =>
        a?.areaID?.areaNameEN?.localeCompare(b?.areaID?.areaNameEN),
      render: (val, row, index) => {
        return row?.areaID?.[`areaName${currentLanguage}`];
      },
    },

    {
      key: "cityID",
      title: t("City Name"),
      sorter: (a, b) =>
        a?.cityID?.cityNameEN?.localeCompare(b?.cityID?.cityNameEN),
      dataIndex: "cityName",
      render: (val, row, index) => {
        return row?.cityID?.[`cityName${currentLanguage}`];
      },
    },

    {
      key: "minOrder",
      title: t("Min. Basket Value"),
      dataIndex: "minOrder",
      sorter: {
        compare: (a, b) => a.minOrder - b.minOrder,
        multiple: 3,
      },
      render: (val) => {
        return new Intl.NumberFormat(currentLanguage, {
          style: "currency",
          currency: "AED",
        }).format(val);
      },
    },

    {
      key: "deliveryFee",
      title: t("Delivery Fee"),
      dataIndex: "deliveryFee",
      sorter: {
        compare: (a, b) => a.deliveryFee - b.deliveryFee,
        multiple: 3,
      },
      render: (val) => {
        return new Intl.NumberFormat(currentLanguage, {
          style: "currency",
          currency: "AED",
        }).format(val);
      },
    },

    {
      key: "deliveryTime",
      title: t("Delivery Time"),
      dataIndex: "deliveryTime",
      sorter: {
        compare: (a, b) => a.deliveryTime - b.deliveryTime,
        multiple: 3,
      },
      render: (val, row, index) => {
        return <RenderTime {...{ val, row, index }} />;
      },
    },
    {
      title: "Actions",
      render: (_, row) => (
        <div className="flex justify-start gap-3">
          <span onClick={() => onEdit(row)}>
            <AppIcon name="edit" className="cursor-pointer" />
          </span>
          <span onClick={() => onDelete(row)}>
            <AppIcon name="trash" className="cursor-pointer" />
          </span>
        </div>
      ),
    },
  ];
  const onEdit = (row) => {
    setSelectedArea(row);
    setOpenEditDeliveryAreaModal(true);
  };
  const onDelete = (row) => {
    if (deletingAreaId) return;
    setIsBulkDelete(false);
    setDeleteModal({ open: true, area: row });
  };

  const handleDeleteArea = async (row) => {
      const areaPayload = {
          cityID: row?.cityID?._id || row?.cityID,
          areaID: row?.areaID?._id || row?.areaID,
          countryID: row?.countryID?._id || row?.countryID,
          storeId: row?.storeId,
          brandId: row?.brandId,
      };
      if (
          !areaPayload.cityID ||
          !areaPayload.areaID ||
          !areaPayload.countryID ||
          !areaPayload.storeId ||
          !areaPayload.brandId
      ) {
          UTILS.errorNotification("Invalid area selected");
          return;
      }

      try {
          setDeletingAreaId(row?._id || row?.areaID?._id || row?.areaID);
          const userDetail = UTILS.getCurrentUser();
          const result = await API.DELETE_STORE_AREA({
              areas: [areaPayload],
              token: userDetail?.token,
          });
          if (result?.success === 1) {
              UTILS.successNotification(result?.message || "Area deleted");
              await onRefreshAreas?.();
              setDeleteModal({ open: false, area: null });
          } else {
              UTILS.errorNotification(errorHandler(result));
          }
      } catch (error) {
          UTILS.errorNotification("Please try again later!");
      } finally {
          setDeletingAreaId(null);
      }
  };
  // Selection is held as ids against the unfiltered list, so narrowing the search
  // never silently drops a row the user already ticked.
  const selectedRows = (data || []).filter((row) => selectedKeys.includes(row?._id));

  const toAreaPayload = (row) => ({
    cityID: row?.cityID?._id || row?.cityID,
    areaID: row?.areaID?._id || row?.areaID,
    countryID: row?.countryID?._id || row?.countryID,
    storeId: row?.storeId,
    brandId: row?.brandId,
  });

  const handleOpenBulkDelete = () => {
    if (selectedRows.length === 0) return;
    setIsBulkDelete(true);
    setDeleteModal({ open: true, area: null });
  };

  const handleDeleteSelectedAreas = async () => {
    const payloads = selectedRows.map(toAreaPayload).filter(
      (p) => p.cityID && p.areaID && p.countryID && p.storeId && p.brandId
    );

    if (payloads.length === 0) {
      UTILS.errorNotification("Invalid areas selected");
      return;
    }

    try {
      const userDetail = UTILS.getCurrentUser();
      const result = await API.DELETE_STORE_AREA({
        areas: payloads,
        token: userDetail?.token,
      });

      if (result?.success === 1) {
        UTILS.successNotification(
          result?.message || `${payloads.length} areas deleted`
        );
        setSelectedKeys([]);
        await onRefreshAreas?.();
        setDeleteModal({ open: false, area: null });
        setIsBulkDelete(false);
      } else {
        UTILS.errorNotification(errorHandler(result));
      }
    } catch (error) {
      UTILS.errorNotification("Please try again later!");
    }
  };

  const Items = useMemo(() => {
    const lowerCasedSearchTerm = searchTerm?.toLowerCase();
    if (!lowerCasedSearchTerm) {
      return data;
    }

    return data?.filter((item) => {
      const { areaID, cityID } = item;
      const matchesSearchTerm =
        (areaID?.areaNameEN || cityID?.cityNameEN || "")
          .toLowerCase()
          .includes(lowerCasedSearchTerm) ||
        (areaID?.areaNameAR || cityID?.cityNameAR || "")
          .toLowerCase()
          .includes(lowerCasedSearchTerm);

      return matchesSearchTerm;
    });
  }, [data, searchTerm]);

  useEffect(() => {
    const data = [];
    addedAreas?.forEach((item, index) => {
      data.push({
        ...item,
        key: index,
        // The delivery-areas listing does not return the document _id, so rows are
        // keyed on the area reference instead, which is unique within a store and
        // survives re-ordering in a way a row index does not.
        _id:
          item?._id ||
          item?.areaID?._id ||
          item?.areaID ||
          `row-${index}`,
      });
    });
    setData(data);
  }, [addedAreas]);

  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedDataRows(selectedRows);
      setSelectedRowsKeys(selectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (selectedRowKeys.length !== 0) {
        setSelectedRowsKeys([]);
      }
    },
  };

  const handSearch = (e) => {
    setSearch(e.target.value);
  };

  const areaCount = data?.length || 0;
  const areaText = `${areaCount} ${t(areaCount <= 1 ? "Area" : "Areas")}`;
  return (
    <>
      <div className="">
        <Spin spinning={isLoading}>
        <div className="my-3 flex flex-col md:flex-row flex-shrink-0 items-center justify-between">
                <AppHeading1>Areas for {selectedStore?.label}</AppHeading1>
                <div className="flex flex-col md:flex-row items-center gap-3">
                    <AppSearchInput
                        value={searchTerm}
                        placeholder={t("search area by name")}
                        onInput={(e) => {
                          handSearch(e);
                            // setPage(1);
                        }}
                    />
                    <div className="md:mt-0 mt-3 flex gap-3">
                        <AppButton onClick={()=>setOpenAddDeliveryAreaModal(true)}>Add Delivery Area</AppButton>
                        {selectedKeys.length > 0 && (
                            <>
                                <AppButton variant="danger" onClick={handleOpenBulkDelete}>
                                    Delete Selected ({selectedKeys.length})
                                </AppButton>
                                <AppButton variant="secondary" onClick={() => setSelectedKeys([])}>
                                    Clear selection
                                </AppButton>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="min-h-0 min-w-0 w-full max-w-full flex-1 bg-white">
                <AppPaginatedTable
                    data={Items}
                    total={Items?.length || 0}
                    page={page}
                    pageSize={pageSize}
                    clientSidePagination={true}
                    onPageChange={(p, ps) => {
                        setPage(p);
                        setPageSize(ps);
                    }}
                    columns={columns}
                    selectable
                    selectedRowKeys={selectedKeys}
                    onSelectionChange={setSelectedKeys}
                />
            </div>
            <AddDeliveryAreaModal
                title={t("Add Delivery Area")}
                open={openAddDeliveryAreaModal}
                onSaved={onRefreshAreas}
                onClose={() => setOpenAddDeliveryAreaModal(false)}
            />
            <DeleteDeliveryAreaModal
                open={deleteModal.open}
                area={
                    isBulkDelete
                        ? `${selectedKeys.length} ${selectedKeys.length === 1 ? "area" : "areas"}`
                        : deleteModal.area?.areaID?.[`areaName${currentLanguage}`]
                }
                onClose={() => {
                    setDeleteModal({ open: false, area: null });
                    setIsBulkDelete(false);
                }}
                onDelete={() =>
                    isBulkDelete
                        ? handleDeleteSelectedAreas()
                        : handleDeleteArea(deleteModal.area)
                }
            />
            <EditDeliveryAreaModal
                title="Edit Delivery Area"
                open={openEditDeliveryAreaModal}
                selectedArea={selectedArea}
                onSaved={onRefreshAreas}
                onClose={() => {
                    setOpenEditDeliveryAreaModal(false);
                    setSelectedArea(null);
                }}
            />

        </Spin>
      </div>
    </>
  );
}

export default AreasTable;
